import React, { useEffect, useMemo } from 'react';
import {
	projectTypeToCanonicalId,
	toProjectType,
} from '@atlassian/jira-common-constants/src/project-types.tsx';
import {
	NOT_ENROLLED,
	VARIATION,
} from '@atlassian/jira-common-util-get-experiment-group/src/index.tsx';
import { JiraEntryPointContainer } from '@atlassian/jira-entry-point-container/src/index.tsx';
import { useEntryPoint } from '@atlassian/jira-entry-point/src/controllers/use-entry-point/index.tsx';
import { UNSAFE_noExposureExp } from '@atlassian/jira-feature-experiments';
import { useProject } from '@atlassian/jira-navigation-apps-sidebar-common/src/controllers/project/index.tsx';
import { useAppEditions } from '@atlassian/jira-tenant-context-controller/src/components/app-editions/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useProductEntitlementDetails } from '@atlassian/jira-tenant-context-controller/src/components/product-entitlement-details/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useMemoizedTrialDaysLeftFromTrialEndTime } from '@atlassian/upgrades-and-purchases/get-trial-days-left';
import { recommendPremiumToStandardModalEntryPoint } from '../entrypoint.tsx';
import { isProjectTypeAndEditionSupported } from './common/utils.tsx';

const RecommendPremiumToStandardModalComponentImpl = () => {
	const cloudID = useCloudId();
	const RecommendPremiumToStandardModalEntryPointParams = useMemo(
		() => ({
			cloudIds: [cloudID],
			isStandaloneOfferingFGEnabled: fg('jira_recommend_standard_query_standalone_offering'),
			isTwcJiraTouchPointFix: fg('twc_jira_touchpoint_fix'),
		}),
		[cloudID],
	);

	const { entryPointActions, entryPointReferenceSubject } = useEntryPoint(
		recommendPremiumToStandardModalEntryPoint,
		RecommendPremiumToStandardModalEntryPointParams,
	);

	// eslint-disable-next-line @atlassian/react-entrypoint/no-load-in-hooks
	useEffect(() => {
		entryPointActions.load();
	}, [entryPointActions]);

	return (
		<JiraEntryPointContainer
			id="growth.jiraRecommendPremiumToStandard"
			packageName="jira-recommend-premium-to-standard-modal"
			entryPointReferenceSubject={entryPointReferenceSubject}
			fallback="unmount"
			runtimeProps={{}}
		/>
	);
};

const ExperimentWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const [config] = UNSAFE_noExposureExp('jira_reverse_trial_ea_updates_during_trial');
	const experimentCohort = useMemo(() => config.get('cohort', 'not-enrolled'), [config]);

	if (experimentCohort === VARIATION && fg('jira_reverse_trial_ea_updates_during_trial_gate')) {
		return null;
	}

	return children;
};

export const RecommendPremiumToStandardModalComponent = () => {
	const { data: project } = useProject();
	const projectType = toProjectType(project?.type);

	const appEditions = useAppEditions();

	const productEntitlementDetails = useProductEntitlementDetails();
	const productKey = projectType && projectTypeToCanonicalId(projectType);
	const entitlementDetails = productKey && productEntitlementDetails?.[productKey];

	const billingSourceSystem = entitlementDetails?.billingSourceSystem;

	const daysLeft = useMemoizedTrialDaysLeftFromTrialEndTime(entitlementDetails?.trialEndTime) ?? 0;
	const eligibleDaysLeft = daysLeft === 1 || daysLeft === 3 || daysLeft === 5;

	const [reccStdConfig] = UNSAFE_noExposureExp('recommend_standard_purchase_in_jira_premium_eot');
	const reccStdExperimentCohort = useMemo(
		() => reccStdConfig.get('cohort', 'not-enrolled'),
		[reccStdConfig],
	);
	// dont preload component if not enrolled, correct edition, software project open, not ccp
	if (
		!isProjectTypeAndEditionSupported(appEditions, project?.type) ||
		reccStdExperimentCohort === NOT_ENROLLED ||
		billingSourceSystem !== 'CCP' ||
		!eligibleDaysLeft
	) {
		return null;
	}

	return (
		<ExperimentWrapper>
			<RecommendPremiumToStandardModalComponentImpl />
		</ExperimentWrapper>
	);
};
